import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { describeSchema, metadataText, parseMetadataText } from "../../utils/datasetMetadata";

// A JSON editor for a dataset's item-metadata schema (`metadata_schema`).
//
// The schema is an arbitrary JSON Schema object, so it is edited as JSON text: the parsed object
// goes into the dataset record (saved with Save Changes), and an empty editor means "no schema"
// (sent as `null`).

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  color: var(--main-font-color);
`;

const Label = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: var(--main-font-color);
`;

const Hint = styled.div`
  font-size: 13px;
  color: #a0a6b1;
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 220px;
  padding: 10px 12px;
  font-family: monospace;
  font-size: 12.5px;
  line-height: 1.5;
  color: var(--main-font-color);
  background-color: var(--main-bg);
  border: 1px solid var(--main-border-color);
  border-radius: 6px;
  outline: none;
  resize: vertical;
  box-sizing: border-box;
`;

const ErrorText = styled.div`
  font-size: 13px;
  color: #e74c3c;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const Small = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  font-size: 12.5px;
  border: 1px solid var(--main-border-color);
  border-radius: 6px;
  background: transparent;
  color: var(--main-font-color);
  cursor: pointer;
`;

/**
 * @param {object} props
 * @param {object|string|null} props.value The schema as stored on the dataset.
 * @param {(schema: object|null) => void} props.onChange Receives the parsed schema, or null to clear.
 * @param {boolean} [props.hideLabel] Hide the built-in title when the caller supplies its own.
 */
export const SchemaEditor = ({ value, onChange = () => {}, hideLabel = false }) => {
  const [text, setText] = useState(() => metadataText(value));
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const textRef = useRef(text);

  textRef.current = text;

  // Keep the editor in step with the dataset record (e.g. it finished loading), without
  // reformatting under the caret while the user types a valid schema.
  useEffect(() => {
    const current = parseMetadataText(textRef.current);
    const currentValue = current.ok ? current.value : undefined;

    if (JSON.stringify(currentValue ?? null) === JSON.stringify(value ?? null)) return;

    setText(metadataText(value));
    setError(null);
  }, [value]);

  const commit = (next) => {
    setText(next);

    if (!next.trim()) {
      setError(null);
      onChange(null);
      return;
    }

    const parsed = parseMetadataText(next);

    if (!parsed.ok) {
      setError(`Invalid JSON: ${parsed.message}`);
      return;
    }

    if (parsed.value === null || typeof parsed.value !== "object" || Array.isArray(parsed.value)) {
      setError("The schema must be a JSON object.");
      return;
    }

    setError(null);
    onChange(parsed.value);
  };

  const copy = async () => {
    if (!text.trim()) return;

    try {
      await navigator.clipboard.writeText(text);
    } catch (error) {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }

    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  };

  const format = () => {
    const parsed = parseMetadataText(text);
    if (!parsed.ok) {
      setError(`Invalid JSON: ${parsed.message}`);
      return;
    }
    commit(metadataText(parsed.value));
  };

  return (
    <Wrapper>
      {hideLabel ? null : <Label>Item metadata schema</Label>}
      <Hint>
        A JSON Schema every item's metadata is validated against when it is added, updated or
        ingested. Leave empty for no schema. {value ? `Currently: ${describeSchema(value)}.` : ""}
      </Hint>

      <Textarea
        spellCheck={false}
        value={text}
        placeholder={'{\n  "type": "object",\n  "properties": {\n    "title": { "type": "string" }\n  }\n}'}
        onChange={(event) => commit(event.target.value)}
      />

      {error ? <ErrorText>{error}</ErrorText> : null}

      <Row>
        <Small type="button" onClick={format} disabled={!text.trim()}>
          Format
        </Small>
        <Small type="button" onClick={copy} disabled={!text.trim()} title="Copy the schema JSON">
          {copied ? "Copied" : "Copy"}
        </Small>
        <Small type="button" onClick={() => commit("")} disabled={!text.trim() && !value}>
          Clear schema
        </Small>
      </Row>
    </Wrapper>
  );
};

export default SchemaEditor;
